import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Video, Loader2, Trash2, User } from "lucide-react";

function fmtSize(bytes) {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  if (mb < 1) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${mb.toFixed(1)} MB`;
}

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const m = Math.floor((Date.now() - then) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

/**
 * Tech-side viewer for customer-uploaded fault videos.
 * Fetches the job's `customer_videos[]` array and streams each one from the
 * backend proxy endpoint. Advisor can also soft-delete a rogue upload.
 */
export default function CustomerVideoViewer({ jobId, videos = [], onChanged }) {
  const [blobUrls, setBlobUrls] = useState({}); // videoId → object URL
  const [loading, setLoading] = useState({});   // videoId → bool
  const [deletingId, setDeletingId] = useState(null);
  const abortRefs = useRef({});

  const active = videos.filter((v) => !v.is_deleted);
  // Show narration first so the tech gets context before the fault clip
  const ordered = [...active].sort((a, b) => {
    const ra = a.role === "narration" ? 0 : 1;
    const rb = b.role === "narration" ? 0 : 1;
    if (ra !== rb) return ra - rb;
    return (a.uploaded_at || "").localeCompare(b.uploaded_at || "");
  });

  useEffect(() => {
    // Lazy-load: only fetch when the user expands / clicks play. To keep the
    // UX one-click we still auto-load the FIRST video since it's usually the
    // main fault clip and the advisor will want to see it immediately.
    if (active.length === 0) return;
    const first = ordered[0];
    if (blobUrls[first.id] || loading[first.id]) return;
    loadVideo(first.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, active.length]);

  useEffect(() => () => {
    // Revoke blob URLs on unmount so we don't leak memory
    Object.values(blobUrls).forEach((u) => URL.revokeObjectURL(u));
    Object.values(abortRefs.current).forEach((c) => c?.abort?.());
  }, [blobUrls]);

  const loadVideo = async (videoId) => {
    if (blobUrls[videoId] || loading[videoId]) return;
    setLoading((p) => ({ ...p, [videoId]: true }));
    const ctrl = new AbortController();
    abortRefs.current[videoId] = ctrl;
    try {
      const r = await api.get(
        `/jobs/${jobId}/customer-videos/${videoId}/stream`,
        { responseType: "blob", signal: ctrl.signal }
      );
      const url = URL.createObjectURL(r.data);
      setBlobUrls((p) => ({ ...p, [videoId]: url }));
    } catch (e) {
      if (e.name !== "CanceledError") {
        toast.error("Couldn't load the video");
      }
    } finally {
      setLoading((p) => ({ ...p, [videoId]: false }));
    }
  };

  const del = async (videoId) => {
    if (!window.confirm("Hide this customer video? (The file stays in the audit log.)")) return;
    setDeletingId(videoId);
    try {
      await api.delete(`/jobs/${jobId}/customer-videos/${videoId}`);
      toast.success("Video hidden");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  if (active.length === 0) return null;

  return (
    <div className="card-default border-primary/40 bg-primary/5" data-testid="customer-video-viewer">
      <div className="px-5 py-4 flex items-center gap-3 border-b border-primary/20">
        <div className="w-10 h-10 rounded-full grid place-items-center bg-primary text-primary-foreground">
          <Video className="w-5 h-5" />
        </div>
        <div>
          <div className="label-overline text-primary">Customer's own recording</div>
          <div className="font-display font-bold text-lg mt-0.5">
            {active.length} fault video{active.length === 1 ? "" : "s"} from your customer
          </div>
        </div>
      </div>
      <div className="p-5 space-y-4">
        {ordered.map((v) => {
          const isNarration = v.role === "narration";
          return (
          <div
            key={v.id}
            className="rounded-lg border border-border overflow-hidden bg-background"
            data-testid={`customer-video-${v.id}`}
          >
            {blobUrls[v.id] ? (
              <video
                src={blobUrls[v.id]}
                controls
                playsInline
                preload="metadata"
                className="w-full max-h-[420px] bg-black"
                data-testid={`customer-video-player-${v.id}`}
              />
            ) : (
              <button
                type="button"
                onClick={() => loadVideo(v.id)}
                disabled={loading[v.id]}
                className="w-full aspect-video bg-black/80 flex items-center justify-center text-white gap-2 text-sm hover:bg-black transition-colors"
                data-testid={`customer-video-load-${v.id}`}
              >
                {loading[v.id]
                  ? <><Loader2 className="w-5 h-5 animate-spin" /> Loading…</>
                  : <><Video className="w-5 h-5" /> Tap to play</>}
              </button>
            )}
            <div className="px-3 py-2 flex items-center gap-2 text-xs">
              <User className="w-3.5 h-3.5 text-primary" />
              <span className="font-semibold">Sent by customer</span>
              <span
                className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${isNarration ? "bg-warning/15 text-warning border border-warning/40" : "bg-primary/15 text-primary border border-primary/30"}`}
                data-testid={`customer-video-role-${v.id}`}
              >
                {isNarration ? "Narration" : "Fault clip"}
              </span>
              <span className="text-muted-foreground font-mono-plex">
                · {timeAgo(v.uploaded_at)} · {fmtSize(v.size_bytes)}
              </span>
              <button
                type="button"
                onClick={() => del(v.id)}
                disabled={deletingId === v.id}
                className="ml-auto text-[11px] text-muted-foreground hover:text-destructive inline-flex items-center gap-1"
                data-testid={`customer-video-delete-${v.id}`}
                title="Hide from job — audit copy retained"
              >
                <Trash2 className="w-3 h-3" /> Hide
              </button>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}
